import React, { useState } from 'react';
import { Box, Avatar, Typography, TextField, Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

function getInitials(firstName, lastName) {
  const f = firstName ? firstName.charAt(0).toUpperCase() : '';
  const l = lastName ? lastName.charAt(0).toUpperCase() : '';
  return f + l || '?';
}

/**
 * CommentCreatingCard — shown while the user is composing a new comment.
 * Pure UI: no socket/editor logic. Receives callbacks from CardsColumn.
 */
const CommentCreatingCard = ({ currentUser, selectedText, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [draftText, setDraftText] = useState('');

  const handleSubmit = () => {
    const trimmed = draftText.trim();
    if (trimmed) {
      onSubmit(trimmed);
      setDraftText('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'warning.main',
        boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
        p: 1.5,
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.7rem',
            bgcolor: currentUser?.color || 'primary.main',
            flexShrink: 0,
          }}
        >
          {getInitials(currentUser?.first_name, currentUser?.last_name)}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
          {currentUser?.first_name} {currentUser?.last_name}
        </Typography>
      </Box>

      {/* Selected text context */}
      {selectedText && (
        <Box
          sx={{
            mb: 1,
            px: 1,
            py: 0.5,
            bgcolor: 'warning.50',
            borderLeft: '2px solid',
            borderColor: 'warning.main',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            "{selectedText}"
          </Typography>
        </Box>
      )}

      {/* Input */}
      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        size="small"
        variant="outlined"
        placeholder={t('comment_input_placeholder')}
        value={draftText}
        onChange={(e) => setDraftText(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem',
            borderRadius: '6px',
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
        <Button
          size="small"
          onClick={onCancel}
          sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 1 }}
        >
          {t('cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={!draftText.trim()}
          onClick={handleSubmit}
          sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', px: 1.5 }}
        >
          {t('comment')}
        </Button>
      </Box>
    </Paper>
  );
};

export default CommentCreatingCard;
